import React, { useState, useEffect } from 'react';
import { invitationApi, oesApi, assessmentApi } from '../../services/api';
import Layout from '../../components/layout/Layout';
import CustomSelect from '../../components/common/CustomSelect';
import Toast from '../../components/Toast';
import ConfirmModal from '../../components/ConfirmModal';
import { useLabels } from '../../providers/LabelsProvider';
import { useT } from '../../providers/I18nProvider';

function Invitations() {
  const labels = useLabels();
  const t = useT();
  const [invitations, setInvitations] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [oesList, setOesList] = useState([]);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState('');
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, type: 'success', message: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [inviteToDelete, setInviteToDelete] = useState(null);
  const [formData, setFormData] = useState({
    assessment_id: '',
    email: '',
    oes_id: '',
    allowed_email_domain: '',
    expires_in_days: 7,
  });

  const role = localStorage.getItem('role');
  const isGlobalAdmin = role === 'global_admin';

  const showToast = (type, message) => {
    setToast({ open: true, type, message });
    setTimeout(() => setToast(prev => ({ ...prev, open: false })), 3000);
  };

  useEffect(() => {
    fetchAssessments();
    fetchInvitations();
  }, [selectedAssessmentId]);


  useEffect(() => {
    if (formData.assessment_id) {
      fetchOES();
    } else {
      setOesList([]);
    }
  }, [formData.assessment_id]);

  const fetchAssessments = async () => {
    try {
      // Load all accessible assessments and OES once, then only keep assessments that have at least one OES
      const [assessmentsResp, oesResp] = await Promise.all([
        assessmentApi.getAll(0, 1000),
        oesApi.getAll(0, 1000)
      ]);

      const allAssessments = assessmentsResp.data || [];
      const allOes = oesResp.data || [];

      const assessmentIdsWithOes = new Set(
        allOes
          .filter(oes => oes.assessment_id != null)
          .map(oes => oes.assessment_id)
      );

      const eligibleAssessments = allAssessments.filter(a =>
        assessmentIdsWithOes.has(a.id)
      );

      setAssessments(eligibleAssessments);

      // If currently selected assessment no longer eligible, clear it
      if (
        selectedAssessmentId &&
        !assessmentIdsWithOes.has(parseInt(selectedAssessmentId))
      ) {
        setSelectedAssessmentId('');
      }
    } catch (err) {
      console.error('Failed to load assessments:', err);
      showToast('error', 'Failed to load assessments');
    }
  };

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      const assessmentId = selectedAssessmentId ? parseInt(selectedAssessmentId) : null;
      const response = await invitationApi.getAll(0, 1000, false, assessmentId);
      setInvitations(response.data || []);
    } catch (err) {
      console.error('Failed to load invitations:', err);
      showToast('error', 'Failed to load invitations');
    } finally {
      setLoading(false);
    }
  };


  const fetchOES = async () => {
    try {
      const assessmentId = formData.assessment_id ? parseInt(formData.assessment_id) : null;
      const response = await oesApi.getAll(0, 1000, null, null, assessmentId);
      setOesList(response.data || []);
    } catch (err) {
      console.error('Failed to load OES:', err);
      showToast('error', 'Failed to load OES');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!formData.assessment_id) {
      showToast('error', 'Please select an assessment');
      return;
    }
    
    if (!formData.email) {
      showToast('error', 'Please enter an email address');
      return;
    }
    
    if (!formData.oes_id) {
      showToast('error', `Please select an ${labels.entityName} for the new user`);
      return;
    }
    
    try {
      await invitationApi.create(
        parseInt(formData.assessment_id),
        formData.email,
        parseInt(formData.oes_id),
        formData.allowed_email_domain || null,
        parseInt(formData.expires_in_days)
      );
      showToast('success', t('admin.invitationCreated'));
      setShowCreateModal(false);
      resetForm();
      fetchInvitations();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to create invitation';
      showToast('error', errorMessage);
    }
  };

  const resetForm = () => {
    setFormData({
      assessment_id: '',
      email: '',
      oes_id: '',
      allowed_email_domain: '',
      expires_in_days: 7,
    });
  };

  const handleDelete = (invite) => {
    setInviteToDelete(invite);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!inviteToDelete) return;
    
    try {
      await invitationApi.delete(inviteToDelete.id);
      showToast('success', t('admin.invitationDeleted'));
      fetchInvitations();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to delete invitation';
      showToast('error', errorMessage);
    } finally {
      setConfirmOpen(false);
      setInviteToDelete(null);
    }
  };

  const copyInviteLink = (token) => {
    const link = `${window.location.origin}/signup?invite=${token}`;
    navigator.clipboard.writeText(link);
    showToast('success', t('admin.invitationLinkCopied'));
  };

  // Get user display name (for new user invitations, shows email)
  const getUserDisplayName = (invite) => {
    return invite.email || 'N/A';
  };

  return (
    <Layout>
      
      <Toast
        open={toast.open}
        setOpen={(open) => setToast(prev => ({ ...prev, open }))}
        type={toast.type}
        className="fixed top-6 right-6 z-[60]"
      >
        {toast.message}
      </Toast>
      
      {/* Page Title */}
      <div className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-eucnet-navy">{t('admin.invitationManagement')}</h1>
              <p className="mt-1 text-sm text-slate-600">{t('admin.invitationMgmtDesc')}</p>
            </div>
            <button
              onClick={() => {
                setShowCreateModal(true);
                resetForm();
              }}
              className="px-6 py-2 bg-eucnet-blue text-white rounded-lg hover:bg-eucnet-navy transition-colors"
            >
              + {t('admin.createInvitation')}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Assessment Filter */}
        {assessments.length > 0 && (
          <div className="mb-6 bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-slate-700">{t('admin.filterByAssessment')}</label>
              <div className="w-64">
                <CustomSelect
                  value={selectedAssessmentId}
                  onChange={(e) => setSelectedAssessmentId(e.target.value)}
                  options={[
                    { value: '', label: t('admin.allAssessments') },
                    ...assessments.map(a => ({ value: String(a.id), label: a.name }))
                  ]}
                  placeholder={t('admin.allAssessments')}
                />
              </div>
            </div>
          </div>
        )}

        {/* Invitations Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] table-fixed divide-y divide-slate-200">
              <thead className="bg-eucnet-grey-100">
                <tr>
                  <th className="w-[20%] px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">{t('common.assessment')}</th>
                  <th className="w-[19%] px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">{t('common.email')}</th>
                  <th className="w-[16%] px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">{labels.entityName}</th>
                  <th className="w-[11%] px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">{t('common.created')}</th>
                  <th className="w-[11%] px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">{t('common.expires')}</th>
                  <th className="w-[11%] px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase">{t('common.status')}</th>
                  <th className="w-[12%] px-4 py-3 text-right text-xs font-medium text-slate-700 uppercase">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-eucnet-blue"></div>
                      <p className="text-slate-500 mt-2">{t('admin.loadingInvitations')}</p>
                    </td>
                  </tr>
                ) : invitations.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <p className="text-slate-500">{t('admin.noInvitations')}</p>
                    </td>
                  </tr>
                ) : (
                  invitations.map((invite) => (
                    <tr key={invite.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <div
                          className="text-sm font-medium text-slate-900 truncate"
                          title={invite.assessment?.name || `Assessment #${invite.assessment_id}`}
                        >
                          {invite.assessment?.name || `Assessment #${invite.assessment_id}`}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div
                          className="text-sm text-slate-900 truncate"
                          title={getUserDisplayName(invite)}
                        >
                          {getUserDisplayName(invite)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div
                          className="text-sm text-slate-900 truncate"
                          title={invite.oes?.name || t('admin.notAssigned')}
                        >
                          {invite.oes?.name || t('admin.notAssigned')}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-500">
                        {new Date(invite.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-500">
                        {new Date(invite.expires_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          invite.is_used
                            ? 'bg-slate-100 text-slate-600'
                            : new Date(invite.expires_at) < new Date()
                            ? 'bg-red-100 text-red-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {invite.is_used ? t('admin.used') : new Date(invite.expires_at) < new Date() ? t('admin.expired') : t('common.active')}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-end gap-3">
                          {!invite.is_used && (
                            <button
                              onClick={() => copyInviteLink(invite.token)}
                              className="text-eucnet-blue hover:text-eucnet-navy transition-colors"
                              title="Copy invitation link"
                            >
                              <svg
                                className="w-5 h-5"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <rect
                                  x="9"
                                  y="3"
                                  width="11"
                                  height="14"
                                  rx="2"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                />
                                <rect
                                  x="4"
                                  y="7"
                                  width="11"
                                  height="14"
                                  rx="2"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                />
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(invite)}
                            className="text-red-600 hover:text-red-700 transition-colors pb-2"
                            title="Delete invitation"
                          >
                            <svg className="w-8 h-8 fill-current" viewBox="0 -3 32 32">
                              <path d="M13 15h2v6h-2zM17 15h2v6h-2z" />
                              <path d="M20 9c0-.6-.4-1-1-1h-6c-.6 0-1 .4-1 1v2H8v2h1v10c0 .6.4 1 1 1h12c.6 0 1-.4 1-1V13h1v-2h-4V9zm-6 1h4v1h-4v-1zm7 3v9H11v-9h10z" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('admin.createInvitation')}</h2>
              <form onSubmit={handleCreate}>
                {/* Assessment Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('common.assessment')} *</label>
                  <CustomSelect
                    value={formData.assessment_id}
                    onChange={(e) => {
                      setFormData({ ...formData, assessment_id: e.target.value, oes_id: '' });
                    }}
                    options={[
                      { value: '', label: t('admin.selectAssessment') },
                      ...assessments.map(a => ({ value: String(a.id), label: a.name }))
                    ]}
                    placeholder={t('admin.selectAssessment')}
                    required
                  />
                </div>

                {/* Email Address */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('admin.emailAddress')} *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="user@example.com"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky"
                    required
                  />
                  <p className="text-xs text-slate-500 mt-1">{t('admin.emailForNewUser')}</p>
                </div>

                {/* OES Selection */}
                {formData.assessment_id && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {labels.entityName} *
                    </label>
                    <CustomSelect
                      value={formData.oes_id}
                      onChange={(e) => setFormData({ ...formData, oes_id: e.target.value })}
                      options={[
                        { value: '', label: `Select ${labels.entityName}` },
                        ...oesList.map(oes => ({ value: String(oes.id), label: oes.name }))
                      ]}
                      placeholder={`Select ${labels.entityName}`}
                      required
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      The user will be assigned to this {labels.entityName} upon signup
                    </p>
                    {oesList.length === 0 && formData.assessment_id && (
                      <p className="text-xs text-amber-600 mt-1">No {labels.entityName} found in this assessment. Please create {labels.entityName} first.</p>
                    )}
                  </div>
                )}

                {/* Email Domain Restriction */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('admin.emailDomainRestriction')}</label>
                  <input
                    type="text"
                    value={formData.allowed_email_domain}
                    onChange={(e) => setFormData({ ...formData, allowed_email_domain: e.target.value })}
                    placeholder="example.com"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky"
                  />
                  <p className="text-xs text-slate-500 mt-1">{t('admin.emailDomainDesc')}</p>
                </div>

                {/* Expiration */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('admin.expiresInDays')}</label>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={formData.expires_in_days}
                    onChange={(e) => setFormData({ ...formData, expires_in_days: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                    className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={!formData.assessment_id || !formData.email || !formData.oes_id}
                    className="flex-1 px-4 py-2 bg-eucnet-blue text-white rounded-lg hover:bg-eucnet-navy disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('common.create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Modal */}
      <ConfirmModal
        open={confirmOpen}
        title={t('admin.deleteInvitation')}
        message={t('admin.deleteInvitationMsg')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        onConfirm={confirmDelete}
        onCancel={() => {
          setConfirmOpen(false);
          setInviteToDelete(null);
        }}
      />
    </Layout>
  );
}

export default Invitations;
